import React from "react";

// Helper to format date for display
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Helper to convert date to ISO string
const toISODateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const OrdersFilterPanel = ({
  showFilters,
  toggleFilters,
  activeFilterTab,
  handleTabChange,
  filters,
  setFilters,
  dateRangeInputs,
  handleDateRangeInputChange,
  applyDateRange,
  clearDateRange,
  clearAllDateRanges,
  applyQuickDateRange,
  hasActiveFilters,
  clearAllFilters,
  handleFilterChange,
  availableFilterOptions,
  filterCounts,
}) => {
  if (!showFilters) return null;

  const filterTabs = [
    { key: "date", label: "Date" },
    { key: "client", label: "Client" },
    { key: "billingEntity", label: "Billing Entity" },
    { key: "projectManager", label: "Project Manager" },
    { key: "projectStatus", label: "Project Status" },
    { key: "paymentStatus", label: "Payment Status" },
    { key: "taskType", label: "Task Type" },
    { key: "country", label: "Country" },
    { key: "poc", label: "POC" },
  ];

  const getFilterLabel = (filterType) => {
    const labels = {
      date: "Created Date",
      invoicedDate: "Invoiced Date",
      paidDate: "Paid Date",
      client: "Client",
      billingEntity: "Billing Entity",
      projectManager: "PM",
      projectStatus: "Status",
      paymentStatus: "Payment",
      taskType: "Task Type",
      country: "Country",
      poc: "POC",
    };
    return labels[filterType] || filterType;
  };

  const dateFilterSections = [
    { key: "date", label: "Project Created", showQuickRanges: true },
    { key: "invoicedDate", label: "Project Invoiced", showQuickRanges: false },
    { key: "paidDate", label: "Project Paid", showQuickRanges: false },
  ];

  const activeDateFilterCount = dateFilterSections.reduce(
    (count, section) => count + (filters[section.key]?.length > 0 ? 1 : 0),
    0
  );
  const hasActiveDateFilters = activeDateFilterCount > 0;

  const renderQuickRanges = (filterKey) => (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Ranges</h4>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => {
            const today = new Date();
            const lastWeek = new Date();
            lastWeek.setDate(today.getDate() - 7);
            applyQuickDateRange(
              filterKey,
              toISODateString(lastWeek),
              toISODateString(today)
            );
          }}
          className="px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Last 7 Days
        </button>

        <button
          onClick={() => {
            const today = new Date();
            const lastMonth = new Date();
            lastMonth.setMonth(today.getMonth() - 1);
            applyQuickDateRange(
              filterKey,
              toISODateString(lastMonth),
              toISODateString(today)
            );
          }}
          className="px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Last 30 Days
        </button>

        <button
          onClick={() => {
            const today = new Date();
            const startMonth = new Date(
              today.getFullYear(),
              today.getMonth(),
              1
            );
            applyQuickDateRange(
              filterKey,
              toISODateString(startMonth),
              toISODateString(today)
            );
          }}
          className="px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          This Month
        </button>

        <button
          onClick={() => {
            const today = new Date();
            const startYear = new Date(today.getFullYear(), 0, 1);
            applyQuickDateRange(
              filterKey,
              toISODateString(startYear),
              toISODateString(today)
            );
          }}
          className="px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Year to Date
        </button>
      </div>
    </div>
  );

  const renderDateRangeSection = ({ key, label, showQuickRanges }) => {
    const range = dateRangeInputs[key] || { start: "", end: "" };
    const hasAppliedRange = (filters[key] || []).length > 0;

    return (
      <div key={key} className="p-3 bg-blue-50 rounded-md space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium text-blue-800">{label}</h4>
          {(range.start || range.end || hasAppliedRange) && (
            <button
              onClick={() => clearDateRange(key)}
              className="text-xs text-blue-700 hover:text-blue-900"
            >
              Clear
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label
              htmlFor={`${key}-range-start`}
              className="block text-xs text-gray-700 mb-1"
            >
              Start Date
            </label>
            <input
              type="date"
              id={`${key}-range-start`}
              value={range.start}
              onChange={(event) =>
                handleDateRangeInputChange(key, "start", event.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          <div>
            <label
              htmlFor={`${key}-range-end`}
              className="block text-xs text-gray-700 mb-1"
            >
              End Date
            </label>
            <input
              type="date"
              id={`${key}-range-end`}
              value={range.end}
              onChange={(event) =>
                handleDateRangeInputChange(key, "end", event.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          <div className="flex space-x-2 pt-1">
            <button
              onClick={() => applyDateRange(key)}
              disabled={!range.start || !range.end}
              className={`flex-1 py-2 text-sm rounded-md ${
                !range.start || !range.end
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Apply Range
            </button>
          </div>
        </div>

        {showQuickRanges && renderQuickRanges(key)}
      </div>
    );
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[1100]"
        onClick={toggleFilters}
      ></div>

      {/* Modal */}
      <div className="fixed inset-0 z-[1101] flex items-start justify-center p-4 pt-16 overflow-y-auto">
        <div className="relative w-11/12 md:w-3/4 lg:w-2/3 max-w-[1200px] h-[82vh] max-h-[calc(100vh-8rem)] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden flex flex-col my-auto">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center shrink-0">
            <h3 className="text-lg font-medium text-gray-800">Filter Projects</h3>
            <button
              onClick={toggleFilters}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 flex flex-col md:flex-row">
              {/* Left side - Filter categories */}
              <div className="w-full md:w-64 border-r border-gray-200 overflow-y-auto shrink-0">
                <ul className="py-2">
                  {filterTabs.map((tab) => (
                    <li
                      key={tab.key}
                      className={`px-4 py-3 cursor-pointer ${activeFilterTab === tab.key
                        ? "bg-blue-50 border-l-4 border-blue-500"
                        : "hover:bg-gray-50"
                        }`}
                      onClick={() => handleTabChange(tab.key)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700">
                          {tab.label}
                        </span>
                        {(tab.key === "date"
                          ? activeDateFilterCount > 0
                          : filters[tab.key]?.length > 0) && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            {tab.key === "date"
                              ? activeDateFilterCount
                              : filters[tab.key].length}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right side - Filter options */}
              <div className="flex-1 min-h-0 p-4 overflow-y-auto">
                <div className="mb-2 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-800">
                    {filterTabs.find((t) => t.key === activeFilterTab)?.label ||
                      activeFilterTab}
                  </h3>

                  {activeFilterTab === "date" && hasActiveDateFilters && (
                    <button
                      onClick={clearAllDateRanges}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear All Date Filters
                    </button>
                  )}

                  {activeFilterTab !== "date" && filters[activeFilterTab]?.length > 0 && (
                    <button
                      onClick={() =>
                        setFilters({ ...filters, [activeFilterTab]: [] })
                      }
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Filter options content */}
                <div className="mt-3">
                  {activeFilterTab === "date" ? (
                    <div className="space-y-4">
                      {dateFilterSections.map(renderDateRangeSection)}
                    </div>
                  ) : (
                    // Checkbox grid for other filter types
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3">
                      {availableFilterOptions[activeFilterTab]?.map((option) => (
                        <div key={option} className="flex items-start">
                          <input
                            id={`${activeFilterTab}-${option}`}
                            type="checkbox"
                            checked={filters[activeFilterTab]?.includes(option)}
                            onChange={() =>
                              handleFilterChange(activeFilterTab, option)
                            }
                            className="h-4 w-4 mt-1 flex-shrink-0 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor={`${activeFilterTab}-${option}`}
                            className="ml-2 text-sm text-gray-700 overflow-hidden text-ellipsis"
                            style={{ wordBreak: "break-word" }}
                          >
                            {option}
                            <span className="ml-1 text-gray-400 text-xs">
                              ({filterCounts[activeFilterTab]?.[option] || 0})
                            </span>
                          </label>
                        </div>
                      ))}
                      {(!availableFilterOptions[activeFilterTab] ||
                        availableFilterOptions[activeFilterTab].length === 0) && (
                          <p className="text-sm text-gray-500 col-span-full">
                            No options available
                          </p>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom section - Selected Filters */}
            <div className="border-t border-gray-200 bg-gray-50 p-3 min-h-[88px] max-h-[128px] overflow-y-auto shrink-0">
              <div className="mb-2 flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Selected Filters
                </span>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-gray-600 hover:text-gray-800"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
              {hasActiveFilters ? (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(filters).map(([filterType, values]) =>
                    values.map((value) => {
                      // Special handling for date range display
                      if (
                        ["date", "invoicedDate", "paidDate"].includes(filterType) &&
                        value.startsWith("range:")
                      ) {
                        const [, startDate, endDate] = value.split(":");
                        const formattedStart = formatDisplayDate(startDate);
                        const formattedEnd = formatDisplayDate(endDate);

                        return (
                          <div
                            key={`${filterType}-${value}`}
                            className="flex items-center bg-gray-100 rounded-full pl-3 pr-2 py-1"
                          >
                            <span className="text-xs text-gray-800">
                              {getFilterLabel(filterType)}: {formattedStart} to{" "}
                              {formattedEnd}
                            </span>
                            <button
                              onClick={() => clearDateRange(filterType)}
                              className="ml-1 text-gray-500 hover:text-gray-700"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        );
                      }

                      // Regular filter chip
                      return (
                        <div
                          key={`${filterType}-${value}`}
                          className="flex items-center bg-gray-100 rounded-full pl-3 pr-2 py-1"
                        >
                          <span className="text-xs text-gray-800">
                            {getFilterLabel(filterType)}: {value}
                          </span>
                          <button
                            onClick={() => handleFilterChange(filterType, value)}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No filters selected yet.</p>
              )}
            </div>
          </div>

          {/* Footer with Apply button */}
          <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 sm:px-6 flex justify-end shrink-0">
            <button
              onClick={toggleFilters}
              className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={toggleFilters}
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrdersFilterPanel;
